import styles from './CmsCustomerWizardFilter.module.scss';
import { useSessionStorage, useTracking } from '@components/hooks';
import { Button as BaseButton } from '@ariakit/react';
import {
  Button,
  ChoiceCard,
  Content,
  Container,
  Heading,
  Pill,
} from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

const handleReset = (setStepNumber, setAnswers, setSessionStorage) => {
  setStepNumber(0);
  setAnswers([]);
  setSessionStorage([]);
};
const handleAnswerPillClick = (
  index,
  setStepNumber,
  answers,
  setAnswers,
  sessionStorage,
  setSessionStorage,
) => {
  const prevStep = sessionStorage[index]?.order || 0;
  setSessionStorage(answers.slice(0, index));
  setStepNumber(prevStep);
  setAnswers(answers.slice(0, index));
};

const QuestionsComponent = ({
  order,
  title,
  subtitle,
  label: optionLabel,
  options,
  sessionStorage,
  setSessionStorage,
  step,
  setStep,
  setAnswers,
  answers,
  isFinalStep,
  headingSize,
}) => {
  const track = useTracking();
  const handleClickNext = (text, param, next) => {
    setStep(prev => prev + next);
    setAnswers([...answers, { text, param, order }]);
    setSessionStorage([...answers, { text, param, order }]);
  };

  useEffect(() => {
    if (step > 0) {
      track({
        event: 'CustomerJourneyWizard',
        answers: answers?.map(answer => answer.text),
        steps: step,
      });
    }
    if (isFinalStep) {
      setSessionStorage([...sessionStorage, 'finalStep']);
    }
  }, [step]);

  return (
    <div className={styles.question}>
      <Heading
        as={headingSize}
        className={styles.title}
        id="ariaQuestionTitle"
        size={headingSize}
      >
        {title}
      </Heading>
      <Content className={styles.subtitle} id="ariaQuestionSubtitle" withParser>
        {subtitle}
      </Content>
      {answers?.length > 0 && (
        <div>
          <div className={styles.pillContainer}>
            <Heading as="span" className={styles.noWrap} noSpacing size="h4">
              {optionLabel || 'Ihre Auswahl'}
            </Heading>
            {answers?.map((answer, index) => (
              <Pill
                className={classNames(styles.noWrap, styles.pills)}
                glyph="times"
                isActive
                key={answer.title}
                onClick={() =>
                  handleAnswerPillClick(
                    index,
                    setStep,
                    answers,
                    setAnswers,
                    sessionStorage,
                    setSessionStorage,
                  )
                }
              >
                {answer.text}
              </Pill>
            ))}
          </div>
          <Button
            className={styles.resetButton}
            onClick={() => handleReset(setStep, setAnswers, setSessionStorage)}
            variant="link"
          >
            zurücksetzen
          </Button>
        </div>
      )}

      <div
        aria-describedby="ariaQuestionTitle ariaQuestionSubtitle"
        className={styles.optionsWrapper}
      >
        {options?.map(({ text, image, links, param, glyph, nextQuestion }) => (
          <ChoiceCard
            as={BaseButton}
            glyph={glyph}
            icon={image}
            image={image}
            key={text}
            links={links}
            onClick={() => handleClickNext(text, param, nextQuestion + 1)}
            title={text}
          />
        ))}
      </div>
    </div>
  );
};

const CustomerWizardFilter = ({ items, initial, mainTitle, mainSubtitle }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const [sessionStorage, setSessionStorage] =
    useSessionStorage('filter-groups');

  useEffect(() => {
    if (initial) {
      const [initialOptions] = items.reduce((acc, item) => {
        const [initialOption] = item.options.filter(
          option => option.param === initial,
        );
        if (initialOption) {
          return [...acc, initialOption];
        }
        return acc;
      }, []);

      const jumpToStep = initialOptions?.nextQuestion
        ? (initialOptions?.nextQuestion || 0) + 1
        : 1;
      setAnswers([...answers, initialOptions]);
      setStep(prev => prev + jumpToStep);
    }
  }, []);

  const questionsToRender = items?.map((item, index) => ({
    ...item,
    order: index,
  }));

  const stepToRender = [
    ...questionsToRender,
    {
      order: items.length,
    },
  ];

  const [CurrentStepComponent] = stepToRender.filter(
    component => component.order === step,
  );
  const questionHeadingSize = mainTitle === '' ? 'h2' : 'h3';
  return (
    <Container className={styles.container}>
      {mainTitle && (
        <Heading as="h2" size="h2">
          {mainTitle}
        </Heading>
      )}
      {mainSubtitle && <Content withParser>{mainSubtitle}</Content>}
      {CurrentStepComponent && (
        <QuestionsComponent
          answers={answers}
          isFinalStep={step === items.length}
          order={CurrentStepComponent.order}
          sessionStorage={sessionStorage}
          setAnswers={setAnswers}
          setSessionStorage={setSessionStorage}
          setStep={setStep}
          step={step}
          {...items[step]}
          headingSize={questionHeadingSize}
        />
      )}
    </Container>
  );
};

export default CustomerWizardFilter;
